import { createFileRoute, Link } from "@tanstack/react-router";
import { ContentPage } from "@/components/content-page";
import { BlogPostsSection } from "@/components/blog-posts-section";
import { getBlogListing, type BlogListing } from "@/lib/case-studies.functions";
import { seo } from "@/lib/seo";
import { collectionPage, breadcrumb } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

type BlogSearch = { page: number };

const POSTS_PER_PAGE = 12;

export const Route = createFileRoute("/blog/")({
  validateSearch: (search: Record<string, unknown>): BlogSearch => {
    const page = Number(search.page);
    return { page: Number.isFinite(page) && page > 1 ? Math.floor(page) : 1 };
  },
  loaderDeps: ({ search }) => ({ page: search.page }),
  loader: ({ deps }): Promise<BlogListing> => getBlogListing({ data: { page: deps.page } }),
  head: ({ loaderData, match }) => {
    const index = (loaderData as BlogListing | undefined)?.index ?? [];
    // Each paginated listing page must be self-canonical so Google keeps
    // /blog?page=2..N (and the older posts they link to) in the index instead
    // of treating them as duplicates of page 1.
    const page = match.search.page;
    return seo({
      title: "Blog: AI Marketing, SEO & GEO Strategy Insights",
      description:
        "Insights on agentic marketing, GEO, AI visibility and autonomous growth from the Axy.digital team.",
      path: "/blog",
      canonical: page > 1 ? `${SITE_URL}/blog?page=${page}` : undefined,
      jsonLd: [
        collectionPage({
          name: "Axy.digital Blog",
          description: "Insights on agentic marketing, GEO, AI visibility and autonomous growth.",
          url: `${SITE_URL}/blog`,
          items: index.map((p) => ({
            name: p.title,
            url: `/blog/${p.slug}`,
          })),
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "Blog", url: "/blog" },
        ]),
      ],
    });
  },
  errorComponent: ({ error }) => (
    <section className="px-6 py-24 text-center text-white/70">{error.message}</section>
  ),
  notFoundComponent: () => (
    <section className="px-6 py-24 text-center text-white/70">Page not found.</section>
  ),
  component: BlogPage,
});

function BlogPagination({ page, totalPages }: { page: number; totalPages: number }) {
  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <Pagination className="mt-14">
      <PaginationContent>
        <PaginationItem>
          <Link
            to="/blog"
            search={{ page: page - 1 }}
            aria-label="Go to previous page"
            aria-disabled={page <= 1}
            className={cn(
              buttonVariants({ variant: "ghost", size: "default" }),
              "gap-1 pl-2.5",
              page <= 1 && "pointer-events-none opacity-50",
            )}
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Previous</span>
          </Link>
        </PaginationItem>

        {pages.map((p) => (
          <PaginationItem key={p}>
            <Link
              to="/blog"
              search={{ page: p }}
              aria-current={p === page ? "page" : undefined}
              className={cn(
                buttonVariants({ variant: p === page ? "outline" : "ghost", size: "icon" }),
              )}
            >
              {p}
            </Link>
          </PaginationItem>
        ))}

        <PaginationItem>
          <Link
            to="/blog"
            search={{ page: page + 1 }}
            aria-label="Go to next page"
            aria-disabled={page >= totalPages}
            className={cn(
              buttonVariants({ variant: "ghost", size: "default" }),
              "gap-1 pr-2.5",
              page >= totalPages && "pointer-events-none opacity-50",
            )}
          >
            <span>Next</span>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

function BlogPage() {
  const { posts, total } = Route.useLoaderData() as BlogListing;
  const { page } = Route.useSearch();

  const totalPages = Math.max(1, Math.ceil(total / POSTS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  // `posts` is already the server-sliced page (12 card records, no body).
  const pagePosts = posts;

  return (
    <ContentPage
      eyebrow="Blog"
      title={
        <>
          Field notes on <span className="text-gradient">agentic marketing</span>
        </>
      }
      description="Long-form thinking on GEO, AI visibility, social autopilot and the future of growth."
    >
      {posts.length === 0 ? (
        <section className="px-6 py-24 text-center text-white/60">
          No posts yet — check back soon.
        </section>
      ) : (
        <>
          <BlogPostsSection items={pagePosts} eyebrow="All articles" title="Latest from the team" />
          <div className="px-6 pb-24">
            <div className="max-w-7xl mx-auto">
              <BlogPagination page={currentPage} totalPages={totalPages} />
            </div>
          </div>
        </>
      )}
    </ContentPage>
  );
}
